const CHEVRON_DOWN_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>';

export const setStatus = (form, message = '', type = 'neutral') => {
    const status = form.querySelector('[data-tool-status]');

    if (!status) {
        return;
    }

    status.textContent = message;
    status.dataset.status = message ? type : '';
};

export const setCopyEnabled = (form, enabled) => {
    form.querySelectorAll('[data-tool-copy]').forEach((button) => {
        button.disabled = !enabled;
    });
};

const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
};

export const bindCopy = (form, getText) => {
    form.querySelectorAll('[data-tool-copy]').forEach((button) => {
        button.addEventListener('click', async () => {
            const text = getText();

            if (!text) {
                return;
            }

            try {
                await copyText(text);
                setStatus(form, 'Скопировано в буфер обмена.', 'success');
            } catch {
                setStatus(form, 'Не удалось скопировать результат.', 'error');
            }
        });
    });
};

export const bindClear = (form, callback) => {
    const button = form.querySelector('[data-tool-clear]');

    if (!button) {
        return;
    }

    button.addEventListener('click', () => {
        form.querySelectorAll('[data-tool-input], [data-tool-output]').forEach((field) => {
            field.value = '';
        });
        setCopyEnabled(form, false);
        setStatus(form);
        callback?.();
    });
};

/**
 * Progressively enhances every native `.tool-select` on the page into a
 * custom listbox. The trigger's arrow icon is generated at runtime (there is
 * no server-rendered markup for it), so it is inlined here rather than
 * pulled from a JS icon-library dependency.
 */
export const initToolSelects = (page) => {
    const controls = [];

    page.querySelectorAll('.tool-select').forEach((select) => {
        if (select.dataset.enhanced === 'true') {
            return;
        }

        select.dataset.enhanced = 'true';

        const control = document.createElement('div');
        const trigger = document.createElement('button');
        const value = document.createElement('span');
        const menu = document.createElement('div');
        const optionButtons = [];
        const menuId = `${select.id || `tool-select-${controls.length + 1}`}-menu`;

        control.className = 'tool-select-control';
        trigger.className = 'tool-select-trigger';
        trigger.type = 'button';
        trigger.id = `${select.id || `tool-select-${controls.length + 1}`}-trigger`;
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', menuId);
        value.className = 'tool-select-trigger__value';
        menu.className = 'tool-select-menu';
        menu.id = menuId;
        menu.setAttribute('role', 'listbox');
        menu.setAttribute('aria-hidden', 'true');

        const close = (restoreFocus = false) => {
            control.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            menu.setAttribute('aria-hidden', 'true');
            optionButtons.forEach((button) => button.setAttribute('tabindex', '-1'));

            if (restoreFocus) {
                trigger.focus();
            }
        };

        const update = () => {
            const selectedOption = select.options[select.selectedIndex];
            value.textContent = selectedOption?.textContent || '';
            optionButtons.forEach((button) => {
                const selected = button.dataset.value === select.value;
                button.classList.toggle('is-selected', selected);
                button.setAttribute('aria-selected', String(selected));
            });
        };

        const open = () => {
            controls.forEach((item) => {
                if (item.control !== control) {
                    item.close();
                }
            });
            control.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
            menu.setAttribute('aria-hidden', 'false');
            optionButtons.forEach((button) => button.setAttribute('tabindex', '0'));
        };

        Array.from(select.options).forEach((option, index) => {
            const button = document.createElement('button');
            button.className = 'tool-select-option';
            button.type = 'button';
            button.dataset.value = option.value;
            button.textContent = option.textContent;
            button.setAttribute('role', 'option');
            button.setAttribute('tabindex', '-1');
            button.addEventListener('click', () => {
                select.value = option.value;
                // Dispatch both: consuming tools listen for either 'input' (live preview
                // updates) or 'change' (submit-driven tools), matching native <select> behavior.
                select.dispatchEvent(new Event('input', { bubbles: true }));
                select.dispatchEvent(new Event('change', { bubbles: true }));
                update();
                close(true);
            });
            button.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    close(true);
                } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    const step = event.key === 'ArrowDown' ? 1 : -1;
                    optionButtons[(index + step + optionButtons.length) % optionButtons.length]?.focus();
                }
            });
            optionButtons.push(button);
            menu.append(button);
        });

        select.classList.add('tool-select--native');
        select.before(control);
        control.append(select, trigger, menu);
        trigger.insertAdjacentHTML('beforeend', CHEVRON_DOWN_SVG);
        trigger.prepend(value);

        const label = select.id ? page.querySelector(`label[for="${select.id}"]`) : null;
        if (label) {
            label.setAttribute('for', trigger.id);
        }

        trigger.addEventListener('click', () => {
            if (control.classList.contains('is-open')) {
                close();
            } else {
                open();
            }
        });
        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                open();
                const selectedIndex = Math.max(0, select.selectedIndex);
                optionButtons[selectedIndex]?.focus();
            } else if (event.key === 'Escape') {
                close();
            }
        });
        select.addEventListener('change', update);
        controls.push({ control, close });
        update();
    });

    page.addEventListener('click', (event) => {
        controls.forEach((item) => {
            if (!item.control.contains(event.target)) {
                item.close();
            }
        });
    });
};
